import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, Marker, Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, MapPin, Search } from 'lucide-react';
import { GOOGLE_MAPS_API_KEY } from '@/lib/maps/config';
import { GOOGLE_MAP_LIBRARIES, GOOGLE_MAP_SCRIPT_ID } from '@/lib/maps/loader';


const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 }; // India centroid
const DEFAULT_ZOOM = 4;
const PICKED_ZOOM = 16;

const MAP_CONTAINER_STYLE: React.CSSProperties = { width: '100%', height: '360px' };

const MAP_OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: false,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  clickableIcons: false,
};

export interface PickedAddress {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip_code: string;
  country: string; // ISO-2 short_name (REQUIRED for Uber quotes)
  latitude: number;
  longitude: number;
  formatted?: string;
}

interface MapLocationPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  initial?: { latitude?: number | string; longitude?: number | string };
  onPick: (addr: PickedAddress) => void;
}

const getComponent = (
  components: google.maps.GeocoderAddressComponent[] | undefined,
  type: string,
  useShort = false
): string => {
  if (!Array.isArray(components)) return '';
  const match = components.find((c) => Array.isArray(c?.types) && c.types.includes(type));
  if (!match) return '';
  return (useShort ? match.short_name : match.long_name) || '';
};

export const parseGooglePlace = (
  place: {
    address_components?: google.maps.GeocoderAddressComponent[];
    geometry?: { location?: google.maps.LatLng | { lat: () => number; lng: () => number } } | null;
    formatted_address?: string;
    name?: string;
  } | null | undefined,
  coords?: { lat: number; lng: number }
): PickedAddress => {
  try {
    const comps = place?.address_components;

    const streetNumber = getComponent(comps, 'street_number');
    const route = getComponent(comps, 'route');
    const street1 =
      [streetNumber, route].filter(Boolean).join(' ').trim() ||
      route ||
      (place?.name ?? '');

    const street2 =
      getComponent(comps, 'subpremise') ||
      getComponent(comps, 'sublocality') ||
      getComponent(comps, 'sublocality_level_1') ||
      getComponent(comps, 'neighborhood') ||
      '';

    const city =
      getComponent(comps, 'locality') ||
      getComponent(comps, 'postal_town') ||
      getComponent(comps, 'administrative_area_level_2') ||
      '';

    const state = getComponent(comps, 'administrative_area_level_1', true);
    const zip_code = getComponent(comps, 'postal_code');
    const country = getComponent(comps, 'country', true); // ISO-2 short_name

    let latitude = coords?.lat;
    let longitude = coords?.lng;
    if ((latitude === undefined || longitude === undefined) && place?.geometry?.location) {
      const loc = place.geometry.location as { lat: () => number; lng: () => number };
      try {
        latitude = typeof loc.lat === 'function' ? loc.lat() : latitude;
        longitude = typeof loc.lng === 'function' ? loc.lng() : longitude;
      } catch {
      }
    }

    return {
      street1: street1 || '',
      street2: street2 || '',
      city: city || '',
      state: state || '',
      zip_code: zip_code || '',
      country: country || '',
      latitude: typeof latitude === 'number' && !Number.isNaN(latitude) ? latitude : 0,
      longitude: typeof longitude === 'number' && !Number.isNaN(longitude) ? longitude : 0,
      formatted: place?.formatted_address || '',
    };
  } catch {
    return {
      street1: '', street2: '', city: '', state: '', zip_code: '', country: '',
      latitude: coords?.lat ?? 0, longitude: coords?.lng ?? 0, formatted: '',
    };
  }
};

const toNum = (v?: number | string): number | undefined => {
  if (v === undefined || v === null || v === '') return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
};

const MapLocationPicker: React.FC<MapLocationPickerProps> = ({
  open, onOpenChange, title = 'Pick a location', initial, onPick,
}) => {
  const hasKey = Boolean(GOOGLE_MAPS_API_KEY);

  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAP_SCRIPT_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAP_LIBRARIES,
    preventGoogleFontsLoading: false,
  });

  const initialLat = toNum(initial?.latitude);
  const initialLng = toNum(initial?.longitude);

  const [marker, setMarker] = useState<{ lat: number; lng: number } | null>(
    initialLat !== undefined && initialLng !== undefined ? { lat: initialLat, lng: initialLng } : null
  );
  const [picked, setPicked] = useState<PickedAddress | null>(null);
  const [geoNote, setGeoNote] = useState<string | null>(null);

  const mapRef = useRef<google.maps.Map | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  const center = useMemo(() => marker || DEFAULT_CENTER, [marker]);

  useEffect(() => {
    if (open) {
      const lat = toNum(initial?.latitude);
      const lng = toNum(initial?.longitude);
      setMarker(lat !== undefined && lng !== undefined ? { lat, lng } : null);
      setPicked(null);
      setGeoNote(null);
    }
  }, [open, initial?.latitude, initial?.longitude]);

  const getGeocoder = useCallback((): google.maps.Geocoder | null => {
    if (typeof google === 'undefined' || !google.maps?.Geocoder) return null;
    if (!geocoderRef.current) geocoderRef.current = new google.maps.Geocoder();
    return geocoderRef.current;
  }, []);

  const reverseGeocode = useCallback((lat: number, lng: number) => {
    const geocoder = getGeocoder();
    if (!geocoder) {
      setPicked(parseGooglePlace(null, { lat, lng }));
      return;
    }
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      try {
        if (status === 'OK' && results && results[0]) {
          setGeoNote(null);
          setPicked(parseGooglePlace(results[0], { lat, lng }));
        } else {
          if (status === 'REQUEST_DENIED' || status === 'OVER_QUERY_LIMIT') {
            setGeoNote(
              'Address lookup is unavailable (Google Maps billing/API not fully enabled). The coordinates were captured — please fill the address fields manually.'
            );
          }
          setPicked(parseGooglePlace(null, { lat, lng }));
        }
      } catch {
        setPicked(parseGooglePlace(null, { lat, lng }));
      }
    });
  }, [getGeocoder]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const onMapUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const onAutocompleteLoad = useCallback((ac: google.maps.places.Autocomplete) => {
    autocompleteRef.current = ac;
  }, []);

  const onPlaceChanged = useCallback(() => {
    try {
      const ac = autocompleteRef.current;
      if (!ac) return;
      const place = ac.getPlace();
      if (!place || !place.geometry?.location) return;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setMarker({ lat, lng });
      setPicked(parseGooglePlace(place, { lat, lng }));
      if (mapRef.current) {
        mapRef.current.panTo({ lat, lng });
        mapRef.current.setZoom(PICKED_ZOOM);
      }
    } catch {
    }
  }, []);

  const onMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarker({ lat, lng });
    reverseGeocode(lat, lng);
  }, [reverseGeocode]);

  const onMarkerDragEnd = useCallback((e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarker({ lat, lng });
    reverseGeocode(lat, lng);
  }, [reverseGeocode]);

  const handleUse = () => {
    if (!picked && !marker) return;
    const result = picked || parseGooglePlace(null, marker || undefined);
    onPick(result);
    onOpenChange(false);
  };

  if (!hasKey) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-4 w-4" /> {title}
          </DialogTitle>
          <DialogDescription>
            Search for an address or click / drag the pin. The structured fields will be filled automatically.
          </DialogDescription>
        </DialogHeader>

        {loadError ? (
          <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
            <MapPin className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Map unavailable — enter the address manually.
            </p>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        ) : !isLoaded ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-3">
            <Autocomplete onLoad={onAutocompleteLoad} onPlaceChanged={onPlaceChanged}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search for an address or place"
                  onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                />
              </div>
            </Autocomplete>

            <div className="overflow-hidden rounded-lg border">
              <GoogleMap
                mapContainerStyle={MAP_CONTAINER_STYLE}
                center={center}
                zoom={marker ? PICKED_ZOOM : DEFAULT_ZOOM}
                options={MAP_OPTIONS}
                onLoad={onMapLoad}
                onUnmount={onMapUnmount}
                onClick={onMapClick}
              >
                {marker && (
                  <Marker position={marker} draggable onDragEnd={onMarkerDragEnd} />
                )}
              </GoogleMap>
            </div>

            {picked && (picked.formatted || picked.street1 || picked.city) && (
              <p className="text-xs text-muted-foreground">
                {picked.formatted || [picked.street1, picked.city, picked.state, picked.country].filter(Boolean).join(', ')}
              </p>
            )}

            {geoNote && (
              <p className="text-xs text-amber-600">{geoNote}</p>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handleUse} disabled={!marker}>
                <MapPin className="h-4 w-4 mr-2" /> Use this location
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MapLocationPicker;
